import { inject, Injectable, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';

import { escapeHtml } from '@common/utils/html-escape';
import { environment } from 'src/environments/environment';

import { OBJECT_ICON_SVG } from './object-icon-svg';
import {
  DEFAULT_OBJECT_ICON_ID,
  iconsByCategory,
  isAccountIconId,
  SystemObjectIconId,
} from './object-icons';

type SafeIcons = Readonly<Record<SystemObjectIconId, SafeHtml>>;

type AccountIconState = 'loading' | 'loaded' | 'failed';

const ACCOUNT_ICON_PROBE_TIMEOUT = 15_000;

const accountIconStates = new Map<string, AccountIconState>();

const accountIconStateChanged$ = new Subject<void>();

let defaultAccountId: number | null = null;

export const getAccountIconUrl = (accountId: number, id: number): string =>
  `${environment.objectIconsUrl}${accountId}/${id}.webp`;

const resolveAccountIconUrl = (id: number, accountId?: number | null): string | null => {
  const account = accountId ?? defaultAccountId;

  return account ? getAccountIconUrl(account, id) : null;
};

const probeAccountIcon = (url: string): AccountIconState => {
  const state = accountIconStates.get(url);
  if (state) {
    return state;
  }

  const image = new Image();
  const settle = (result: AccountIconState) => {
    clearTimeout(timeout);
    image.onload = null;
    image.onerror = null;
    if (accountIconStates.get(url) === 'loading') {
      accountIconStates.set(url, result);
      accountIconStateChanged$.next();
    }
  };
  const timeout = setTimeout(() => settle('failed'), ACCOUNT_ICON_PROBE_TIMEOUT);

  image.onload = () => settle('loaded');
  image.onerror = () => settle('failed');
  accountIconStates.set(url, 'loading');
  image.src = url;

  return 'loading';
};

export const hasAccountIconImage = (id: number, accountId?: number | null): boolean => {
  const url = resolveAccountIconUrl(id, accountId);

  return !!url && probeAccountIcon(url) === 'loaded';
};

const getImageIconHtml = (src: string): string =>
  `<img class="object-icon__image" width="32px" height="auto" src="${escapeHtml(src)}"/>`;

export const getObjectIconHtml = (id: number, accountId?: number | null): string => {
  if (isAccountIconId(id)) {
    const url = resolveAccountIconUrl(id, accountId);

    return url && probeAccountIcon(url) === 'loaded'
      ? getImageIconHtml(url)
      : OBJECT_ICON_SVG[DEFAULT_OBJECT_ICON_ID]!;
  }

  const defaultIconHtml = OBJECT_ICON_SVG[id as SystemObjectIconId];

  if (defaultIconHtml) {
    return defaultIconHtml;
  }

  return getImageIconHtml(`common/assets/objects/iconsV2/${id}.webp`);
};

@Injectable({ providedIn: 'root' })
export class ObjectIconsService {
  private readonly sanitizer = inject(DomSanitizer);

  public readonly availableObjectIcons: SafeIcons;

  private readonly accountIconsVersion = signal(0);

  private readonly accountIconsHtml = new Map<string, SafeHtml>();

  readonly accountIconsChanged$ = accountIconStateChanged$.asObservable();

  constructor() {
    const iconIds = Object.values(iconsByCategory).flat() as SystemObjectIconId[];
    const availableIconIds: SystemObjectIconId[] = [DEFAULT_OBJECT_ICON_ID, ...iconIds];

    this.availableObjectIcons = Object.freeze(
      Object.fromEntries(
        availableIconIds.map((id) => [
          id,
          this.sanitizer.bypassSecurityTrustHtml(getObjectIconHtml(id)),
        ]),
      ),
    ) as SafeIcons;

    accountIconStateChanged$.subscribe(() =>
      this.accountIconsVersion.update((version) => version + 1),
    );
  }

  setDefaultAccountId(accountId: number | null): void {
    if (defaultAccountId !== accountId) {
      defaultAccountId = accountId;
      accountIconStateChanged$.next();
    }
  }

  getObjectIcon(id: number, accountId?: number | null): SafeHtml {
    return this.findObjectIcon(id, accountId) ?? this.getDefaultObjectIcon();
  }

  findObjectIcon(id: number, accountId?: number | null): SafeHtml | null {
    if (!isAccountIconId(id)) {
      return this.availableObjectIcons[id as SystemObjectIconId] ?? null;
    }

    this.accountIconsVersion();

    const url = resolveAccountIconUrl(id, accountId);
    if (!url || probeAccountIcon(url) !== 'loaded') {
      return null;
    }

    let html = this.accountIconsHtml.get(url);
    if (!html) {
      html = this.sanitizer.bypassSecurityTrustHtml(getImageIconHtml(url));
      this.accountIconsHtml.set(url, html);
    }

    return html;
  }

  isAccountIconLoading(id: number, accountId?: number | null): boolean {
    this.accountIconsVersion();

    const url = resolveAccountIconUrl(id, accountId);

    return !!url && probeAccountIcon(url) === 'loading';
  }

  retryAccountIcon(id: number, accountId?: number | null): void {
    const url = resolveAccountIconUrl(id, accountId);
    if (url && accountIconStates.get(url) === 'failed') {
      accountIconStates.delete(url);
      accountIconStateChanged$.next();
    }
  }

  getDefaultObjectIcon(): SafeHtml {
    return this.availableObjectIcons[DEFAULT_OBJECT_ICON_ID];
  }
}
